import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getChatListAdminById } from "../../Redux/Actions/chatAction";
import Socket from "../../Global Functions/socket";
import TextInput from "../../Global Functions/textInput";
import { MdPerson, MdEmail, MdPhone } from "react-icons/md";
import toast from "react-hot-toast";
import "./chatComponentAdmin.css";

const ChatComponentAdmin = ({
  selectedBookingId,
  chatWith = "builders",
  selectedPerson,
}) => {
  const dispatch = useDispatch();
  const [messages, setMessages] = useState([]);
  const [inputMessages, setInputMessages] = useState("");

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const messageListRef = useRef(null);
  const currentPageRef = useRef(1);
  const prevBookingIdRef = useRef(null);
  const pendingRef = useRef(new Set());

  const { individualChat } = useSelector((state) => state.chats || {});
  const { admin } = useSelector((state) => state.admin || {});

  const sendMessages = () => {
    if (!inputMessages?.trim()) return toast.error("Please enter a message!");
    if (!selectedBookingId) return toast.error("No conversation selected!");

    const trimmedMessage = inputMessages.trim();

    const messagePayload = {
      message: trimmedMessage,
      builder_id: chatWith === "builders" ? selectedBookingId : undefined,
      user_id: chatWith === "users" ? selectedBookingId : undefined,
      project_id: "67f8edfe9a1b8d002d117673",
      chat_with: chatWith,
      admin_id: admin?.data?._id || "",
    };

    setInputMessages(""); // ✅ clear input immediately

    // ✅ track pending message
    pendingRef.current.add(trimmedMessage);

    // ✅ add optimistically with temp id
    const tempId = `temp-${Date.now()}`;
    setMessages((prev) => [
      {
        ...messagePayload,
        created_at: new Date().toISOString(),
        _id: tempId,
      },
      ...prev,
    ]);

    Socket.emit("help-support-chat", messagePayload); // ✅ no callback needed
  };

  const fetchDetails = useCallback(
    (page, limit, bookingId) => {
      if (!bookingId) return;
      const chatData = {
        page,
        limit,
        ...(chatWith === "builders"
          ? { builder_id: bookingId }
          : { user_id: bookingId }),
      };
      dispatch(getChatListAdminById(chatData));
    },
    [chatWith, dispatch]
  );

  useEffect(() => {
    if (!selectedBookingId) return;

    if (prevBookingIdRef.current !== selectedBookingId) {
      prevBookingIdRef.current = selectedBookingId;
      currentPageRef.current = 1;
      setPagination({ current: 1, pageSize: 10, total: 0 });
      setMessages([]);
      pendingRef.current.clear();
    }

    fetchDetails(1, 10, selectedBookingId);
  }, [selectedBookingId, fetchDetails]);

  useEffect(() => {
    if (!individualChat) return;

    if (individualChat?.total != null) {
      setPagination((prev) => ({ ...prev, total: individualChat.total }));
    }

    if (Array.isArray(individualChat?.data)) {
      const isFirstPage = currentPageRef.current === 1;
      setMessages((prev) =>
        isFirstPage ? individualChat.data : [...prev, ...individualChat.data]
      );
    }
  }, [individualChat]);

  useEffect(() => {
    const messageList = messageListRef.current;
    if (!messageList) return;

    const handleScroll = () => {
      const nearTop = messageList.scrollTop < 50;
      const hasMore = messages.length < pagination.total;

      if (nearTop && hasMore) {
        const nextPage = currentPageRef.current + 1;
        currentPageRef.current = nextPage;
        setPagination((prev) => ({ ...prev, current: nextPage }));
        fetchDetails(nextPage, pagination.pageSize, selectedBookingId);
      }
    };

    messageList.addEventListener("scroll", handleScroll);
    return () => messageList.removeEventListener("scroll", handleScroll);
  }, [messages.length, pagination.total, pagination.pageSize, selectedBookingId, fetchDetails]);

  useEffect(() => {
    if (!selectedBookingId) return;

    const listener = (message) => {
      setMessages((prev) => {
        // ✅ admin sent this — replace temp optimistic with real
        if (message.admin_id && pendingRef.current.has(message.message)) {
          pendingRef.current.delete(message.message);
          return prev.map((m) =>
            m._id?.startsWith("temp-") && m.message === message.message
              ? { ...message }
              : m
          );
        }

        // ✅ incoming from user — check duplicate by _id
        if (message._id) {
          const isDuplicate = prev.some((m) => m._id === message._id);
          if (isDuplicate) return prev;
        }

        return [message, ...prev];
      });
    };

    Socket.on(`${selectedBookingId}`, listener);
    return () => Socket.off(`${selectedBookingId}`, listener);
  }, [selectedBookingId]);

  const getInitials = (name) =>
    name
      ? name
          .split(" ")
          .map((w) => w[0])
          .join("")
          .slice(0, 2)
          .toUpperCase()
      : "?";

  const hasPersonData =
    selectedPerson &&
    typeof selectedPerson === "object" &&
    selectedPerson.name;

  return (
    <div className="cca-window">
      {/* ── Top Bar ── */}
      <div className="cca-topbar">
        {hasPersonData ? (
          <>
            <div className="cca-topbar-avatar">
              {getInitials(selectedPerson.name)}
            </div>

            <div className="cca-topbar-info">
              <div className="cca-topbar-name">
                <MdPerson size={15} />
                {selectedPerson.name}
              </div>
              <div className="cca-topbar-meta">
                <span className="cca-topbar-meta-item">
                  <MdEmail size={12} />
                  {selectedPerson.email || "—"}
                </span>
                <span className="cca-topbar-sep">|</span>
                <span className="cca-topbar-meta-item">
                  <MdPhone size={12} />
                  {selectedPerson.countryCode} {selectedPerson.contactNumber || "—"}
                </span>
              </div>
            </div>

            <div className="cca-topbar-status">
              <span className="cca-status-dot" />
              Active
            </div>
          </>
        ) : (
          <div className="cca-topbar-placeholder">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <p>Select a conversation to start messaging</p>
          </div>
        )}
      </div>

      {/* ── Messages ── */}
      <div className="cca-messages" ref={messageListRef}>
        {!selectedBookingId ? (
          <div className="cca-empty-state">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <p>No conversation selected</p>
            <span>Choose a chat from the sidebar to begin messaging</span>
          </div>
        ) : messages.length === 0 ? (
          <div className="cca-empty-state">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <p>No messages yet</p>
            <span>Send a message to start the conversation</span>
          </div>
        ) : (
          messages.map((chat, index) => {
            const isAdmin = !!chat?.admin_id;
            const key = chat?._id || `${chat?.created_at}-${index}`;

            return (
              <div
                key={key}
                className={`cca-bubble-wrap ${
                  isAdmin ? "cca-bubble-wrap--admin" : "cca-bubble-wrap--user"
                }`}
              >
                {!isAdmin && (
                  <div className="cca-mini-avatar cca-mini-avatar--user">
                    {getInitials(selectedPerson?.name)}
                  </div>
                )}

                <div
                  className={`cca-bubble ${
                    isAdmin ? "cca-bubble--admin" : "cca-bubble--user"
                  }`}
                >
                  <p className="cca-bubble-text">{chat?.message}</p>
                  <span className="cca-bubble-time">
                    {chat?.created_at && !isNaN(new Date(chat.created_at))
                      ? new Date(chat.created_at).toLocaleString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                          month: "short",
                          day: "numeric",
                        })
                      : ""}
                  </span>
                </div>

                {isAdmin && (
                  <div className="cca-mini-avatar cca-mini-avatar--admin">A</div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* ── Input Area ── */}
      <div className="cca-input-area">
        <div className="cca-input-wrapper">
          <TextInput
            value={inputMessages}
            placeholder="Type your message…"
            onChange={(event) => setInputMessages(event.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendMessages();
              }
            }}
          />
        </div>
        <button
          className="cca-send-btn"
          onClick={sendMessages}
          disabled={!selectedBookingId}
          title="Send message (Enter)"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ width: "46px", height: "30px" }}
          >
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default ChatComponentAdmin;